import { z } from "zod";

// Credential fields per provider, hidden when a saved connection is picked
// (the server copies them from that connection).
const CREDENTIAL_FIELDS = {
  gcs: ["google_application_credentials", "google_project_id"],
  s3: ["aws_access_key_id", "aws_secret_access_key", "aws_session_token", "region_name", "s3_endpoint"],
  azure: ["account_name", "account_key"],
};
const BUCKET_FIELD = { gcs: "bucket", s3: "bucket", azure: "container" };

/** Folder to propose for a target storage next to a source one. */
const suggestExportPrefix = (connection, projectId) => {
  let base = (connection.prefix || "").replace(/^\/+/, "").replace(/\/+$/, "");
  // Older storages may carry the bucket name inside the prefix.
  if (connection.bucket && (base === connection.bucket || base.startsWith(`${connection.bucket}/`))) {
    base = base.slice(connection.bucket.length).replace(/^\/+/, "");
  }
  return base ? `${base}/annotations/` : `annotations/project-${projectId}/`;
};

/**
 * Add a "Connection" choice at the top of each provider form when the
 * organisation already has connections for that provider. Picking one
 * sends ``copy_credentials_from`` instead of credentials, fills the bucket
 * (and region / endpoint for S3), and, for a target storage, proposes a
 * folder for the annotations.
 */
export const withSavedConnections = (providers, connections, { target, projectId } = {}) => {
  if (!connections?.length) return providers;
  const out = {};
  for (const [name, config] of Object.entries(providers)) {
    const list = connections.filter((c) => c.type === name);
    const credentialFields = CREDENTIAL_FIELDS[name];
    if (!list.length || !credentialFields) {
      out[name] = config;
      continue;
    }
    const bucketField = BUCKET_FIELD[name];
    const connectionField = {
      name: "copy_credentials_from",
      type: "select",
      label: "Connection",
      description:
        "Reuse a bucket this organisation already connected. Its credentials are copied on the server and never shown here.",
      schema: z.string().optional().default(""),
      resetConnection: true,
      options: [
        { value: "", label: "New connection (enter credentials below)" },
        ...list.map((c) => ({ value: c.ref, label: c.label })),
      ],
      prefillOnChange: (value, formData) => {
        const c = list.find((x) => x.ref === value);
        if (!c) return {};
        const extra = { [bucketField]: c.bucket };
        if (name === "s3") {
          extra.region_name = c.region_name || "";
          extra.s3_endpoint = c.s3_endpoint || "";
        }
        if (target === "export") {
          extra.prefix = suggestExportPrefix(c, projectId);
          if (!formData.title) extra.title = `${c.bucket} annotations`;
        } else if (!formData.prefix) {
          extra.prefix = c.prefix || "";
        }
        return extra;
      },
    };
    const fields = config.fields.map((f) => {
      if (credentialFields.includes(f.name)) {
        return { ...f, visibleWhen: { field: "copy_credentials_from", value: (v) => !v } };
      }
      if (f.name === bucketField) {
        // The connection fills this in, but the same account usually reads
        // other buckets too, so it stays editable.
        return {
          ...f,
          description: `${f.description ? `${f.description} ` : ""}Filled from the connection you pick; change it to use another bucket the same credentials can reach.`,
        };
      }
      return f;
    });
    out[name] = {
      ...config,
      fields: [connectionField, ...fields],
      layout: [{ fields: ["copy_credentials_from"] }, ...(config.layout || [])],
    };
  }
  return out;
};
